import {BaseElement, BaseElementAsync, Label, MsgLabel, MsgOver, OSkinableInit} from "back/commons/basis";
import {ITaskUiEnv} from "back/wsp/views/itemMain";
import {IReg, REG} from "lib/commons/registry";
import {IView, IViewContainer, OViewVisitOptions} from "lib/commons/views";
import {DOM, ENodeType, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {AddComment, ETaskStage, ExecTrans, IUpdateTask, JTaskStates, LIFECYCLE, PutUserResp, RemoveUserResp, SetDeadline, SetDescription, SetScheduleDt, SetTitle, TASK} from "lib/wsp/lcTask";
import {OWedEditorBoxConfig, WedEditorBox} from "back/edit/wed/wedEditorBox";
import {OWedManagerConfig} from "back/edit/wed/wedEditor";
import {ptrItemTagsDefined, WspDataTransferWedAnalyzer} from "back/wsp/widgets/wed/ptrItem";
import {boxTagsDefined} from "back/edit/wed/wedlets/box/boxTags";
import {txtTagsDefined} from "back/edit/wed/wedlets/txt/txt";
import {WED} from "back/edit/wed/wedCore";
import {DocHolder} from "lib/edit/docHolder";
import {IDatasResp, ItemTypeTask} from "lib/wsp/wspMetaUi";
import {Button, ButtonActions, OButtonActionsInit, OButtonInit} from "back/commons/widgets/buttons";
import {SrcExecTransition} from "back/wsp/actions/lcTaskActions";
import {InfoSrcUriMoved, IShortDescCtx} from "lib/wsp/item";
import {POPUP} from "back/commons/widgets/popups";
import {OWedEditorTxtConfig} from "back/edit/wed/features/txtSel";
import {IInfo, IInfoConsumer, IInfoProducer} from "lib/commons/infos";
import {Schema} from "lib/edit/schema/schema";
import {OUserRefInit, UserRef} from "back/core/widgets/userRef";
import {UserSelector} from "back/core/dialogs/userSelector";
import {EUserType, JUser, USER} from "lib/core/user";
import {EAnnotLevel} from "lib/edit/schema/schemaAnnots";
import {IChildWedlet, IParentWedlet} from "back/edit/wed/wedlets/wedlet";
import {JSrcFields, SRC} from "lib/wsp/src";
import {ERROR} from "lib/core/errorReport";
import {IWspXmlHouse} from "lib/wsp/wspsLive";
import {OWedEditorDiffBarConfig} from "back/edit/wed/features/diffBar";

/**
 * Affichage des champs simples des tasks en lecture seule
 */
export interface TaskField extends BaseElement {
	initialize(init: OTaskFieldInit): this;
}

export interface OTaskFieldInit extends OSkinableInit {
	reg?: IReg<ITaskUiEnv>
	field: 'actTi' | 'tkDeadline' | 'scheduledDt' | 'lcSt'
}

export class TaskField extends BaseElement implements IView {
	reg: IReg<ITaskUiEnv>;

	config: OTaskFieldInit;

	_field: HTMLElement;

	_initialize(init: OTaskFieldInit) {
		this.reg = this.findReg(init);
		this.config = init;
		this.setAttribute("field", this.config.field);
		if (this.config.field === "actTi") {
			//na
		} else if (this.config.field === "lcSt") {
			this._field = this.appendChild(<c-label/>); //î={{noShadow:true} as OLabelableConfigs}
		} else {
			//tkDeadline et scheduledDt
			this._field = this.appendChild(<input/>);
		}
	}

	protected _refresh() {
		const itemType = this.reg.env.itemType;
		const longDesc = this.reg.env.longDesc;
		//console.log("longDesc:::::", longDesc);
		switch (this.config.field) {
		case 'actTi':
			DOM.setTextContent(this, longDesc.itTi || "");
			break;
		case 'lcSt':
			const lc = itemType.getLcStateOrUnknown(longDesc.lcSt);
			(this._field as Label).label = lc.name;
			(this._field as Label).icon = lc.iconUrl;
			break;
		case 'tkDeadline':
			DOM.setAttr(this._field, "value", longDesc.tkDeadline.toString());
			break;
		case 'scheduledDt':
			const ct = this.reg.env.taskContent;
			DOM.setAttr(this._field, "value", TASK.getScheduledDtFromContent(ct));
			break;
		}
	}

}

//REG.reg.registerSkin("wsp-task-field", 1, /* language=CSS */ ``);

customElements.define("wsp-task-field", TaskField);


/**
 * Affichage des champs simples des tasks en mode éditable.
 */
export interface TaskFieldInput extends BaseElement {
	initialize(init: OTaskFieldInit): this;
}

export interface OTaskFieldInputInit extends OSkinableInit {
	reg?: IReg<ITaskUiEnv>
	field: 'actTi' | 'tkDeadline' | 'scheduledDt'
	/** true par défaut */
	editable?: boolean
	/** Si true, accepte que le champ soit en erreur à l'enregistrement.
	 * false par défaut */
	permissive?: boolean
	writePerms?: string | string[]
}

export class TaskFieldInput extends BaseElement implements ITaskEditableField, IInfoConsumer {
	reg: IReg<ITaskUiEnv>;

	get fieldName() {return this.config.field}

	get value(): string {
		if (this.config.field === 'actTi') return this._input.value;
		const dt = this._input.valueAsDate;
		return dt ? dt.getTime().toString() : "";
	}

	get valueTime(): number {
		if (this.config.field === 'actTi') return 0;
		const dt = this._input.valueAsDate;
		return dt ? dt.getTime() : 0;
	}

	originalValue: string;

	get isDirty(): boolean {return this.originalValue !== this.value}

	config: OTaskFieldInputInit;

	_input: HTMLInputElement;

	currentStage?: ETaskStage;

	_initialize(init: OTaskFieldInputInit) {
		this.reg = this.findReg(init);
		const sr = this._attach(this.localName, init);
		this.config = init;
		this.setAttribute("field", this.config.field);
		const st = this.reg.env.initStates;
		if (this.config.field === "actTi") {
			this.originalValue = this.reg.env.longDesc.actTi || "";
			this._input = sr.appendChild(<input required/> as HTMLInputElement);
			if (st && st.ti != null) this._input.value = st.ti;
			else this._input.value = this.originalValue;
		} else {
			//tkDeadline et scheduledDt
			this._input = sr.appendChild(<input type="date"/> as HTMLInputElement);
			if (this.config.field === "scheduledDt") {
				this.originalValue = TASK.getScheduledDtFromContent(this.reg.env.taskContent);
				this._input.required = true;
				this.currentStage = this.reg.env.itemType.getLcStateOrUnknown(this.reg.env.longDesc.lcSt).taskStage;
				this._input.value = this.timeToInputValue(st && st.scheduledDt ? st && st.scheduledDt.toString() : this.originalValue);
			} else {
				const longDesc = this.reg.env.longDesc;
				this.originalValue = longDesc.tkDeadline > 0 ? longDesc.tkDeadline.toString() : "";
				this._input.value = this.timeToInputValue(st && st.deadlinedDt ? st && st.deadlinedDt.toString() : this.originalValue);
			}
		}
		this.addEventListener("input", this.onInputChange);
		this.reg.env.taskEditBroker.addConsumer(this);
	}

	onInfo(info: IInfo): void {
		if (info instanceof InfoReqStateTask) {
			if (this.config.field === 'scheduledDt') {
				if (this.currentStage === ETaskStage.forthcoming) {
					if (!info.isDirty) info.isDirty = this.isDirty;
					if (!info.isInError && this._input.valueAsDate == null && !this.config.permissive) info.isInError = true;
					if (info.states && this.isDirty) info.states.scheduledDt = this.valueTime;
				}
			} else {
				if (!info.isDirty) info.isDirty = this.isDirty;
				if (this.config.field === "actTi") {
					if (!info.isInError && !this.config.permissive) info.isInError = !this.value;
					if (info.states && this.isDirty) info.states.ti = this.value;
				} else {
					if (info.states && this.isDirty) info.states.deadlinedDt = this.valueTime;
				}
			}
		} else if (info instanceof InfoReqFillUpdates) {
			if (this.isDirty) {
				switch (this.config.field) {
				case 'actTi':
					info.updates.push(new SetTitle(this.value));
					break;
				case 'tkDeadline':
					info.updates.push(new SetDeadline(this.value));
					break;
				case 'scheduledDt':
					if (this.currentStage === ETaskStage.forthcoming) info.updates.push(new SetScheduleDt(this.value));
					break;
				}
			}
		} else if (info instanceof InfoChangeTask && info.field.fieldName === "lcSt" && this.config.field === "scheduledDt") {
			this.currentStage = this.reg.env.itemType.getLcStateOrUnknown(info.field.value).taskStage;
			this.refresh();
		}
	}

	protected onInputChange(ev: Event) {
		this.reg.env.taskEditBroker.dispatchInfo(new InfoChangeTask(this), this);
	}

	protected _refresh() {
		if (this.config.field === "scheduledDt") {
			const hidden = this.currentStage !== ETaskStage.forthcoming;
			TASK.hideContextField(this, hidden);
			if (hidden) return;
		}
		DOM.setAttrBool(this._input, "readonly", this.config.editable === false || (this.config.writePerms && !this.reg.hasPerm(this.config.writePerms)));
	}

	protected timeToInputValue(val: string): string {
		if (!val) return "";
		try {
			const dt = new Date(parseInt(val));

			function toStr(n: number) {return n < 10 ? "0" + n.toString() : n.toString()}

			return `${dt.getFullYear()}-${toStr(dt.getMonth() + 1)}-${toStr(dt.getDate())}`;
		} catch (e) {
			return "";
		}
	}

}

REG.reg.registerSkin("wsp-task-field-input", 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	:host([hidden]) {
		display: none !important;
	}

	input {
		flex: 1;
		display: flex;
		background-color: var(--edit-bgcolor);
		color: var(--edit-color);
		border: 1px solid var(--border-color);
		padding: .3em;
		text-overflow: ellipsis;
		font-size: inherit;
	}

	input[type="date"] {
		flex: unset;
	}

	input[readonly] {
		background-color: initial;
		border: initial;
	}

	input:invalid {
		border: 1px solid var(--error-color);
		box-shadow: 0 0 2px var(--error-color);
	}
`);

customElements.define("wsp-task-field-input", TaskFieldInput);


/** Affiche dans un formulaire d'édition de la tâche l'état du cycle de vie courant et permet de choisir une transition définissant le prochain état. */

export interface OTaskFieldLcInit extends OButtonActionsInit<TaskFieldLc> {
	reg?: IReg<ITaskUiEnv>
	/** true par défaut */
	editable?: boolean
	/** Si true, accepte que le champ soit en erreur à l'enregistrement.
	 * false par défaut */
	permissive?: boolean
	writePerms?: string | string[]
}

export class TaskFieldLc extends ButtonActions<TaskFieldLc> implements ITaskEditableField, IShortDescCtx, IInfoConsumer {
	reg: IReg<ITaskUiEnv>;

	config: OTaskFieldLcInit;

	selectedTrans: SrcExecTransition<IShortDescCtx>;

	get fieldName() {return 'lcSt' as 'lcSt'}

	get value(): string {return this.selectedTrans ? this.selectedTrans.trans.targetState : this.reg.env.longDesc.lcSt}

	get label() {
		return this.reg.env.itemType.getLcStateOrUnknown(this.value).name;
	}

	get icon() {
		return this.reg.env.itemType.getLcStateOrUnknown(this.value).iconUrl;
	}

	get shortDescs() {return this.reg.env.srcView.shortDescs}

	get emitter() {return this}

	onInfo(info: IInfo): void {
		if (info instanceof InfoReqStateTask) {
			if (this.selectedTrans) {
				info.isDirty = true;
				if (info.states) info.states.trans = this.selectedTrans.trans.code;
			}
		} else if (info instanceof InfoReqFillUpdates) {
			if (this.selectedTrans) info.updates.push(new ExecTrans(this.selectedTrans.trans.code));
		}
	}

	protected _initialize(init: OTaskFieldLcInit): void {
		this.reg = this.findReg(init);
		this.config = init;
		const st = this.reg.env.initStates;
		if (SRC.isNewSrcUri(this.reg.env.longDesc.srcUri)) {
			init.actions = this.reg.env.itemType.getCreateTransitions().map((tr) => new SrcExecTransition(tr, this.reg.env.itemType, this.execTr));
			init.groupOrder = this.reg.env.itemType.getLcTransitionsGroupOrder();
			if (st && st.trans) this.selectedTrans = init.actions.find((a: SrcExecTransition<IShortDescCtx>) => a.trans.code === st.trans) as SrcExecTransition<IShortDescCtx>;
			if (!this.selectedTrans) this.selectedTrans = init.actions[0] as SrcExecTransition<IShortDescCtx>;
		} else {
			init.actions = LIFECYCLE.listAllTransitionActions(this.reg.env.itemType, this.execTr);
			init.groupOrder = this.reg.env.itemType.getLcTransitionsGroupOrder();
			if (st && st.trans) this.selectedTrans = init.actions.find((a: SrcExecTransition<IShortDescCtx>) => a.trans.code === st.trans) as SrcExecTransition<IShortDescCtx>;
		}
		init.skinOver = this.localName;
		super._initialize(init);
		this.reg.env.taskEditBroker.addConsumer(this);
	}

	protected _refresh() {
		super._refresh();
		this.disabled = this.config.editable === false || (this.config.writePerms && !this.reg.hasPerm(this.config.writePerms));
	}

	protected execTr(this: SrcExecTransition<TaskFieldLc>, ctx: TaskFieldLc) {
		ctx.selectedTrans = this;
		ctx.refresh();
		ctx.reg.env.taskEditBroker.dispatchInfo(new InfoChangeTask(ctx), ctx);
	}
}

REG.reg.registerSkin("wsp-task-field-lc", 1, /* language=CSS */ `
	:host {
		color: var(--edit-color);
	}

	:host([disabled]) {
		opacity: unset;
	}
`);

customElements.define("wsp-task-field-lc", TaskFieldLc);


/**
 * Affichage des champs simples des tasks en mode éditable.
 */
export interface TaskFieldUsers extends BaseElement {
	initialize(init: OTaskFieldUsersInit): this;
}

export interface OTaskFieldUsersInit extends OSkinableInit {
	reg?: IReg<ITaskUiEnv>
	resp: string
	/** true par défaut */
	editable?: boolean
	/** Si true, accepte que le champ soit en erreur à l'enregistrement.
	 * false par défaut */
	permissive?: boolean
	writePerms?: string | string[]
	hideErrors?: boolean
}

export class TaskFieldUsers extends BaseElementAsync implements ITaskEditableField, IInfoConsumer {
	reg: IReg<ITaskUiEnv>;

	config: OTaskFieldUsersInit;

	respConf: IDatasResp;
	originalUsers: Set<string>;
	currentUsers: Set<string>;
	currentLcSt: string;
	msg: MsgLabel;

	get fieldName() {return 'resps' as 'resps'}

	get value() {return this.config.resp}

	get isDirty() {
		if (!this.currentUsers) return false;
		if (this.originalUsers.size !== this.currentUsers.size) return true;
		for (const u of this.originalUsers) if (!this.currentUsers.has(u)) return true;
		return false;
	}

	get isInError() {
		const count = this.shadowRoot.querySelectorAll<HTMLElement>('c-userref:not([data-isEmpty])').length;
		if (count > 1) return !this.isCardMulti;
		return count === 0 && this.isRequired;
	}

	isCardMulti: boolean;

	get isRequired() {
		const required = this.respConf.required;
		return required instanceof RegExp ? required.test(this.currentLcSt) : required || false;
	}

	protected async _initialize(init: OTaskFieldUsersInit) {
		this.reg = this.findReg(init);
		this.config = init;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		this.respConf = this.reg.env.itemType.getResp(this.config.resp);
		if (!this.respConf) {
			this.hidden = true;
			return;
		}
		this.isCardMulti = this.respConf.userCard === 'multi';

		this.originalUsers = TASK.getUsersFromContent(this.reg.env.taskContent, this.config.resp);
		this.currentLcSt = this.reg.env.longDesc.lcSt;

		const ctn = sr.appendChild(<div id="ctn"/>);
		this.msg = sr.appendChild(<c-msg hidden/>) as MsgLabel;

		const states = this.reg.env.initStates;
		const accounts = states && states.resps && states.resps[this.config.resp];
		if (accounts) this.currentUsers = new Set(accounts);

		const users = this.originalUsers.size > 0 ? await this.reg.env.universe.useUsers.getUserSet(Array.from(this.currentUsers || this.originalUsers)) : [];
		users.sort(this.sortUsers);

		if (this.config.editable === false) {
			for (const u of (this.currentUsers || this.originalUsers)) {
				ctn.appendChild(<UserRef î={{reg: this.reg, nickOrAccount: u} as OUserRefInit}/>);
			}
		} else {
			for (const u of (this.currentUsers || this.originalUsers)) {
				//Si pb config card=single mais >1 users, on garde le bouton suppr dans tous les cas.
				ctn.appendChild(<div class="user">
					<UserRef î={{reg: this.reg, nickOrAccount: u} as OUserRefInit}/>
					<button class="remUser" onclick={this.onRemUser} title="Supprimer"/>
				</div>);
			}
			if (this.isCardMulti) {
				ctn.appendChild(<button class="addUsers" onclick={this.onAddUsers} title="Ajouter des utilisateurs"/>);
			} else {
				ctn.setAttribute("card", "single");
				if (!ctn.firstElementChild) ctn.appendChild(<div class="user">
					<UserRef î={{reg: this.reg} as OUserRefInit}/>
				</div>);
				ctn.appendChild(<button class="selectUser" onclick={this.onSelectUser} title="Sélectionner l'utilisateur"/>);
				(ctn.firstElementChild as HTMLElement).onclick = this.onSelectUser;
			}
		}
		this.reg.env.taskEditBroker.addConsumer(this);
	}

	sortUsers(u1: JUser, u2: JUser): number {
		return USER.getPrimaryName(u1).localeCompare(USER.getPrimaryName(u2));
	}

	insertUser(user: JUser) {
		const ctn = this.shadowRoot.getElementById('ctn');
		let next: HTMLElement = DOM.findFirstChild(ctn, IS_user);
		while (next && this.sortUsers((next.firstElementChild as UserRef).user, user) < 0) {
			next = DOM.findNextSibling(ctn, IS_user);
		}
		ctn.insertBefore(<div class="user">
			<UserRef î={{reg: this.reg, user} as OUserRefInit}/>
			<button class="remUser" onclick={this.onRemUser} title="Supprimer"/>
		</div>, next || ctn.lastElementChild);
	}

	onRemUser(this: HTMLButtonElement, ev: Event) {
		ev.stopImmediatePropagation();
		const me = DOMSH.findHost<TaskFieldUsers>(this);
		const userRef = this.previousElementSibling as UserRef;
		if (!me.currentUsers) me.currentUsers = new Set(me.originalUsers);
		me.currentUsers.delete(userRef.nickOrAccount);
		userRef.parentElement.remove();
		me.refresh();
		me.reg.env.taskEditBroker.dispatchInfo(new InfoChangeTask(me), me);
	}

	async onAddUsers(this: HTMLButtonElement, ev: Event) {
		ev.stopImmediatePropagation();
		const me = DOMSH.findHost<TaskFieldUsers>(this);
		const {UserSelector} = await import("back/core/dialogs/userSelector.js");
		const userSelector = new UserSelector().initialize({
			reg: me.reg,
			userGrid: {
				usersSrv: me.reg.env.universe.useUsers,
				grid: {selType: "multi"},
				filterGroups: me.respConf.usersSelection?.restrictFromGroups,
				filterType: me.respConf.usersSelection?.userType === 'groups' ? EUserType.group : (me.respConf.usersSelection?.userType === 'users' ? EUserType.user : null),
				filterTypeInputVisibility: false
			},
			selectAndCloseOnDblClick: true
		});
		const users = await POPUP.showMenuFromEvent<JUser[] | null>(userSelector, ev || me, me, null, { initWidth: '20em'}).onNextClose();
		if (users) {
			if (!me.currentUsers) me.currentUsers = new Set(me.originalUsers);
			for (const u of users) {
				if (!me.currentUsers.has(u.account)) {
					me.currentUsers.add(u.account);
					me.insertUser(u);
				}
			}
			me.refresh();
			me.reg.env.taskEditBroker.dispatchInfo(new InfoChangeTask(me), me);
		}
	}

	async onSelectUser(this: HTMLButtonElement, ev: Event) {
		ev.stopImmediatePropagation();
		const me = DOMSH.findHost<TaskFieldUsers>(this);
		const {UserSelector} = await import("back/core/dialogs/userSelector.js");
		const userSelector = new UserSelector().initialize({
			reg: me.reg,
			userGrid: {
				usersSrv: me.reg.env.universe.useUsers,
				filterGroups: me.respConf.usersSelection?.restrictFromGroups,
				filterType: me.respConf.usersSelection?.userType === 'groups' ? EUserType.group : (me.respConf.usersSelection?.userType === 'users' ? EUserType.user : null),
			},
			selectAndCloseOnDblClick: true
		});
		const users = await POPUP.showMenuFromEvent<JUser[] | null>(userSelector, ev || me, me, null, { initWidth: '20em'}).onNextClose();
		if (users && users[0]) {
			if (!me.currentUsers) me.currentUsers = new Set();
			else me.currentUsers.clear();
			me.currentUsers.add(users[0].account);
			const userRef = me.shadowRoot.querySelector('c-userref') as UserRef;
			userRef.user = users[0];
			//cleanup des suivants si anomalie
			let elt: HTMLElement;
			while ((elt = DOM.findNextSibling(userRef.parentElement, IS_user))) elt.remove();
			me.refresh();
			me.reg.env.taskEditBroker.dispatchInfo(new InfoChangeTask(me), me);
		}
	}

	onInfo(info: IInfo): void {
		if (info instanceof InfoReqStateTask) {
			if (!info.isDirty && this.isDirty) info.isDirty = true;
			if (!info.isInError && this.isInError && !this.config.permissive) info.isInError = true;
			if (info.states && this.isDirty) {
				if (!info.states.resps) info.states.resps = {};
				info.states.resps[this.respConf.code] = Array.from(this.currentUsers);
			}
		} else if (info instanceof InfoReqFillUpdates) {
			if (this.currentUsers) {
				for (const u of this.currentUsers) {
					if (!this.originalUsers.has(u)) info.updates.push(new PutUserResp(u, this.config.resp));
				}
				for (const u of this.originalUsers) {
					if (!this.currentUsers.has(u)) info.updates.push(new RemoveUserResp(u, this.config.resp));
				}
			}
		} else if (info instanceof InfoChangeTask && info.field.fieldName === "lcSt") {
			this.currentLcSt = info.field.value;
			this.refresh();
		}
	}

	protected _refresh() {
		super._refresh();
		DOM.setAttrBool(this, "readonly", this.config.editable === false || (this.config.writePerms && !this.reg.hasPerm(this.config.writePerms)));
		const users = this.shadowRoot.querySelectorAll<UserRef>('c-userref');
		const count = users.length;
		let msg = null;
		if (count > 1 && !this.isCardMulti) msg = "Un seul utilisateur doit être renseigné";
		else if ((count === 0 || !users.item(0).nickOrAccount) && this.isRequired) msg = this.isCardMulti ? "Au moins un utilisateur doit être renseigné" : "Un utilisateur doit être renseigné";
		this.msg.setCustomMsg(msg, 'error');
	}
}

function IS_user(n: Node): n is HTMLElement {return DOM.IS_element(n) && n.classList.contains('user')}

REG.reg.registerSkin("wsp-task-field-users", 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	#ctn {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-wrap: wrap;
	}

	.user {
		display: flex;
		min-height: 0;
		min-width: 0;
		margin: .2em;
		border: 1px solid var(--border-color);
		border-radius: .1em;
		padding: .1em;
	}

	c-userref {
		color: var(--edit-color);
	}

	button {
		border: none;
		cursor: pointer;
		width: 1.2em;
		height: 1.2em;
		align-self: center;
		font-size: inherit;
	}

	.remUser {
	  align-self: flex-start;
	  background: transparent no-repeat url("/@skin@/commons/icons/delete.svg") top;
	  background-size: 65%;
  }

  .addUsers {
	  background: transparent no-repeat url("/@skin@/core/actions/users/add-user.svg") center;
		height: var(--icon-size);
    width: var(--icon-size);
    min-height: var(--icon-size);
    min-width: var(--icon-size);
    background-size: contain;
 }

  .selectUser {
	  background: transparent no-repeat var(--dropdown-url) center;
	  background-size: 1em 1em;
  }

  c-msg {
	  justify-content: flex-start;
	  font-style: italic;
  }

  [card=single] > .user {
	  cursor: pointer;
		user-select: none;
	}

	[card=single] > :only-of-type > .remUser {
		display: none;
	}

	:host([readonly]) button {
		display: none;
	}
`);

customElements.define("wsp-task-field-users", TaskFieldUsers);


/**
 * Champs XML des taches (affiché via un Wed et une house volatile)
 */
export interface TaskXmlField extends BaseElement {
	initialize(init: OTaskXmlFieldInit): this;
}

export interface OTaskXmlFieldInit extends OSkinableInit {
	reg?: IReg<ITaskUiEnv>
	/** true par défaut */
	editable?: boolean
	/** Si true, accepte que le champ soit en erreur à l'enregistrement.
	 * false par défaut */
	permissive?: boolean
	field: 'tkDescription' | 'tkComment'
	editorKey: string
	editorConfig?: OWedEditorBoxConfig
	wedMgrConfig?: OWedManagerConfig
	writePerms?: string | string[]
}

export class TaskXmlField extends BaseElementAsync implements IView, IViewContainer, ITaskEditableField, IInfoConsumer {
	reg: IReg<ITaskUiEnv>;

	view: WedEditorBox;

	house: IWspXmlHouse;

	fieldName: 'tkDescription' | 'tkComment';

	config: OTaskXmlFieldInit;

	get value(): Element {return this.house.document.documentElement}

	get isDirty(): boolean {return this._initedAsDirty || this.house.isDirty}

	protected _initedAsDirty: boolean;

	async _initialize(init: OTaskXmlFieldInit) {
		try {
			this.config = init;
			this.reg = this.findReg(init);
			this.fieldName = init.field;
			if (init.editable !== false) {
				this.reg.env.taskEditBroker.addConsumer(this);
				this.setAttribute("editable", "");
			}
			this.attachShadow(DOMSH.SHADOWDOM_INIT);
			this._initAndInstallSkin(this.localName, init);

			const wsp = this.reg.env.wsp;
			const longDesc = this.reg.env.longDesc;
			const itemType = wsp.wspMetaUi.getItemType(longDesc.itModel) as ItemTypeTask;

			const dataEditor = itemType.getEditor(init.editorKey);
			if (!dataEditor) throw Error("No editor found for task desc: " + init.editorKey);

			const editorConfig = Object.assign({}, init.editorConfig) as OWedEditorBoxConfig & OWedEditorDiffBarConfig;
			const wedMgrConfig = Object.assign({}, init.wedMgrConfig) as OWedManagerConfig;

			//if (!editorConfig.metaBarMode) editorConfig.metaBarMode = 'available';

			if (!wedMgrConfig.fetchContentThreshold) wedMgrConfig.fetchContentThreshold = 6000;
			if (!wedMgrConfig.dataTransferAnalyzer) wedMgrConfig.dataTransferAnalyzer = WspDataTransferWedAnalyzer.SINGLETON;

			this.view = new WedEditorBox();

			if (dataEditor.initReg) {
				//Customisation du reg pour l'editor.
				this.reg = REG.createSubReg(this.reg);
				await dataEditor.initReg(this.reg, this.view);
			}

			if (dataEditor.overrideConfigs) await dataEditor.overrideConfigs(editorConfig, wedMgrConfig, this.view);

			//Après dataEditor.overrideConfigs() pour empêcher de surcharger editorConfig.reg qui doit être fait dans dataEditor.initReg()
			if (!editorConfig.reg) editorConfig.reg = this.reg;

			//chargement asynchrone en // des librairies standard wed et des wedModels
			this.view.addAsyncLib(boxTagsDefined);
			this.view.addAsyncLib(txtTagsDefined);
			this.view.addAsyncLib(ptrItemTagsDefined);
			const mainEndP = itemType.getWed(dataEditor.mainWed);
			this.view.addAsyncLib(WED.loadWedModel(mainEndP, mainEndP).then((wed) => this.view.wedModel = wed));
			// if (dataEditor.outlineWed) {
			// 	let outlineEndP = itemType.getWed(dataEditor.outlineWed);
			// 	this.view.addAsyncLib(WED.loadWedModel(outlineEndP, outlineEndP).then((wed) => this.view.outlineWedModel = wed));
			// }
			// if (dataEditor.previewWed) {
			// 	let altviewEndP = itemType.getWed(dataEditor.previewWed);
			// 	this.view.addAsyncLib(WED.loadWedModel(altviewEndP, altviewEndP).then((wed) => this.view.previewWedModel = wed));
			// }

			wedMgrConfig.readOnlyCauses = [];
			editorConfig.hideDiffBar = true; //Pas de diff sur une tâche
			editorConfig.withoutScrolls = true; //XXX Permettre de pointer un scroll parent
			if (init.editable === false) {
				wedMgrConfig.readOnlyCauses.push("initView");
				editorConfig.hideCommonBar = true;
				editorConfig.hideErrBar = true;
				editorConfig.hideUnknownBar = true;
				editorConfig.disableBoxSelection = true;
				editorConfig.disableVirtuals = true;
				(editorConfig as OWedEditorTxtConfig).hideStackBar = true;
			} else if (!this.reg.hasPerm(init.writePerms || "write.task.update")) {
				wedMgrConfig.readOnlyCauses.push("perms");
			}

			let doc: Document;
			const st = this.reg.env.initStates;
			let schema: Schema;
			if (init.field === "tkDescription") {
				schema = await itemType.getDesriptionSchema();
				if (st && st.desc) {
					this._initedAsDirty = true;
					doc = DOM.parseDomValid(st.desc);
				}
				if (!doc) {
					const originalDesc = TASK.getDescriptionFromContent(this.reg.env.taskContent);
					//console.log("originalDesc::::::", originalDesc);
					doc = DOM.newDomDoc();
					if (originalDesc) doc.appendChild(doc.importNode(originalDesc, true));
					else doc.appendChild(doc.createElementNS(TASK.TASK_NS, TASK.TAG_setDescription));
				}
			} else {
				schema = await itemType.getCommentSchema();
				if (st && st.cmt) {
					this._initedAsDirty = true;
					doc = DOM.parseDomValid(st.cmt);
				}
				if (!doc) {
					doc = DOM.newDomDoc();
					doc.appendChild(doc.createElementNS(TASK.TASK_NS, TASK.TAG_addComment));
				}
			}
			this.house = await wsp.wspServer.wspsLive.newLocalHouse({
				initialDoc: doc,
				schema,
				buildOptions: {genAnnots: init.editable !== false, autoMutate: true, autoComplete: true, autoNormXml: true, autoNormChars: true, autoCleanup: true}
			}, wsp);
			this.view.initialize(editorConfig);
			if (await this.view.initFromDocHolder(new DocHolder(this.house), wedMgrConfig)) {
				this.shadowRoot.appendChild(this.view);
			} else {
				throw Error("Init editor failed");
			}

			this.house.listeners.on("dirtyChange", () => {this.reg.env.taskEditBroker.dispatchInfo(new InfoChangeTask(this), this)})
		} catch (e) {
			console.log(e);
			this.closeEditor();
		}
	}

	connectedCallback() {
		super.connectedCallback();
		// Comment masqué à la création
		if (this.fieldName === 'tkComment' && SRC.isNewSrcUri(this.reg.env.longDesc.srcUri)) {
			TASK.hideContextField(this, true);
		}
	}

	onInfo(info: IInfo): void {
		if (info instanceof InfoReqStateTask) {
			switch (this.fieldName) {
			case 'tkDescription':
				if (!info.isInError && !this.config.permissive) info.isInError = this.house.schemaDom.getAllAnnots().find((a) => a.level.weight >= EAnnotLevel.error.weight) != null;
				if (info.states) {
					if (this.isDirty) {
						info.isDirty = true;
						info.states.desc = DOM.ser(this.house.getDocumentForSave());
					}
				} else if (!info.isDirty) info.isDirty = this.isDirty;
				break;
			case 'tkComment':
				if (info.states) {
					if (!this.isCommentEmpty()) {
						info.isDirty = true;
						info.states.cmt = DOM.ser(this.house.getDocumentForSave());
					}
				} else if (!info.isDirty) info.isDirty = !this.isCommentEmpty();
				break;
			}
		} else if (info instanceof InfoReqFillUpdates) {
			if (this.isDirty) {
				switch (this.fieldName) {
				case 'tkDescription':
					info.updates.push(new SetDescription(this.value));
					break;
				case 'tkComment':
					if (!this.isCommentEmpty()) {
						info.updates.push(new AddComment(this.value));
					}
					break;
				}
			}
		}
	}

	isCommentEmpty(): boolean {
		let isEmpty = true;
		this.view.wedMgr.rootWedlet.visitWedletChildren(0, Infinity, (w: IChildWedlet) => {
			if (w.model.nodeName === TASK.TAG_addComment) {
				(w as IParentWedlet).visitWedletChildren(0, Infinity, (w: IChildWedlet) => {
					if (w.model.nodeType === ENodeType.element) {
						(w as IParentWedlet).visitWedletChildren(0, Infinity, (w: IChildWedlet) => {
							if (!w.isEmpty()) {
								isEmpty = false;
								return 'stop';
							}
						});
						return 'stop';
					}
				});
				return 'stop';
			}
		});
		return isEmpty;
	}

	closeEditor() {
		if (this.house) {
			this.reg.env.universe.wspServer.wspsLive.removeLocalHouse(this.house);
			this.house = null;
		}
		if (!this.view) {
			this.view.wedMgr.killEditor();
			if (this.view.parentNode) this.view.remove();
			this.view = null;
		}
	}

	onViewHidden(closed?: boolean) {
		if (closed) this.closeEditor();
	}

	visitViews(visitor: (view: IView) => any): any {
		if (this.view) return visitor(this.view);
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return this.view ? visitor(this.view) : Promise.resolve(undefined);
	}
}

REG.reg.registerSkin("wsp-task-xmlfield", 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	:host([editable]) {
		border: 1px solid var(--border-color);
	}
`);


customElements.define("wsp-task-xmlfield", TaskXmlField);

/**
 * Bouton d'enregistrement d'une édition d'un formulaire (à placer généralement avec wsp-task-cancel).
 */
export class TaskSaveBtn extends Button implements IInfoConsumer {
	reg: IReg<ITaskUiEnv>;

	_initialize(init: OButtonInit) {
		this.reg = this.findReg(init);
		setDefaultBtn(init, "Valider les modifications", "/@skin@/wsp/views/tasks/save.svg");
		this.hidden = true;
		super._initialize(init);
		this.onclick = this.onClick;
		this.reg.env.taskEditBroker.addConsumer(this);
	}

	onInfo(info: IInfo): void {
		if (info instanceof InfoChangeTask) {
			this.disabled = info.state.isInError;
			if (DOM.setHidden(this, !info.state.isDirty)) {
				this.reg.env.srcView.setNeedValid(info.state.isDirty);
				this.refresh();
			}
		}
	}

	async onClick(ev: MouseEvent) {
		if (this.hidden) return;
		if (this.disabled) {
			POPUP.showNotifWarning("Des erreurs doivent être corrigées avant de pouvoir valider les modifications.", this);
			return;
		}
		const env = this.reg.env;
		const req = new InfoReqFillUpdates();
		env.taskEditBroker.dispatchInfo(req, this);
		if (req.updates.length > 0) {
			try {
				const {srcUri} = await new MsgOver().setStandardMsg("saving").showMsgOver(env.srcView).waitFor<JSrcFields>(
					TASK.saveTask(env.wsp, this, SRC.srcRef(env.longDesc), req.updates)
				);
				if (srcUri !== env.longDesc.srcUri) {
					//cas d'une création de task.
					//console.log("new srcUri:::",srcUri);
					this.reg.env.srcView.resetMainView();
					this.reg.env.infoBroker.dispatchInfo(new InfoSrcUriMoved(env.longDesc.srcUri, srcUri), this);
				}
			} catch (e) {
				ERROR.report("Échec à la validation de la tâche", e);
				throw e;
			}
		}
	}
}

customElements.define("wsp-task-save", TaskSaveBtn);

/**
 * Bouton d'annulation d'une édition d'un formulaire (pendant de wsp-task-save).
 */
export class TaskCancelBtn extends Button implements IInfoConsumer {
	reg: IReg<ITaskUiEnv>;

	_initialize(init: OButtonInit) {
		this.reg = this.findReg(init);
		setDefaultBtn(init, "Annuler les modifications", "/@skin@/wsp/views/tasks/cancel.svg");
		this.hidden = true;
		super._initialize(init);
		this.onclick = this.onClick;
		this.reg.env.taskEditBroker.addConsumer(this);
	}

	onInfo(info: IInfo): void {
		if (info instanceof InfoChangeTask) {
			this.hidden = !info.state.isDirty || SRC.isNewSrcUri(this.reg.env.longDesc.srcUri);
		}
	}

	async onClick(ev: MouseEvent) {
		if (this.hidden) return;
		if (await POPUP.confirm("Confirmez l'abandon des modifications de cette tâche", this, {okLbl: "Abandonner mes changements"})) {
			this.reg.env.srcView.redrawMainView();
		}
	}

}

customElements.define("wsp-task-cancel", TaskCancelBtn);


/**
 * Bouton de suppression définitive d'une task.
 */
export class TaskDeleteBtn extends Button implements IInfoConsumer {
	reg: IReg<ITaskUiEnv>;

	_initialize(init: OButtonInit) {
		this.reg = this.findReg(init);
		setDefaultBtn(init, "Supprimer cette tâche", "/@skin@/wsp/views/tasks/delete.svg");
		super._initialize(init);
		const hasPerm = this.reg.hasPerm("ui.task.fields.deleteBtn");
		if (hasPerm) {
			this.onclick = this.onClick;
			this.reg.env.taskEditBroker.addConsumer(this);
		}
		DOM.setAttrBool(this, "hidden", !hasPerm);
	}

	onInfo(info: IInfo): void {
		if (info instanceof InfoChangeTask) {
			this.hidden = info.state.isDirty;
		}
	}

	async onClick(ev: MouseEvent) {
		if (this.hidden) return;
		if (await POPUP.confirm("Souhaitez-vous vraiment supprimer DÉFINITIVEMENT cette tâche ?", this, {okLbl: "Supprimer"})) {
			const env = this.reg.env;
			new MsgOver().setStandardMsg("deleting").showMsgOver(env.srcView).waitFor(
				TASK.deleteTask(env.wsp, this, [env.longDesc.srcId])
			);
		}
	}
}

customElements.define("wsp-task-delete", TaskDeleteBtn);

function setDefaultBtn(init: OButtonInit, label: string, icon: string) {
	if (init.uiContext === 'dialog') {
		if (init.label === undefined) init.label = label;
	} else {
		if (init.title === undefined) init.title = label;
	}
	if (init.icon === undefined) init.icon = icon;
}

/**
 * Bouton d'exécution d'une transition précise, indépendamment de tout formulaire
 * et autres input en cours d'édition et bouton wsp-task-save.
 */
export interface TaskExecTransBtn extends Button {
	initialize(init: OTaskExecTransBtnInit): this;
}

export interface OTaskExecTransBtnInit extends OButtonInit {
	reg?: IReg<ITaskUiEnv>
	transition: string
	noConfirm?: boolean
}

export class TaskExecTransBtn extends Button {
	reg: IReg<ITaskUiEnv>;

	config: OTaskExecTransBtnInit;

	trans: SrcExecTransition<IShortDescCtx>;

	_initialize(init: OTaskExecTransBtnInit) {
		this.config = init;
		this.reg = this.findReg(init);
		super._initialize(init);
		const trans = this.reg.env.itemType.getLcTransition(this.config.transition);
		if (trans) {
			this.trans = new SrcExecTransition(trans, this.reg.env.itemType);
			this.onclick = this.onClick;
			if (!this.label) this.label = trans.name;
		}
	}

	buildInitFromAtts(init?: OTaskExecTransBtnInit): any {
		init = super.buildInitFromAtts(init) as OTaskExecTransBtnInit;
		init.transition = this.getAttribute("transition");
		init.noConfirm = this.getAttribute("confirm") === "no";
		return init;
	}

	async onClick(ev: MouseEvent) {
		if (this.config.noConfirm || await POPUP.confirm(`Confirmez votre choix '${this.label}'`, this)) {
			try {
				await TASK.saveTask(this.reg.env.wsp, this, this.reg.env.longDesc.srcId, [new ExecTrans(this.config.transition)]);
			} catch (e) {
				ERROR.report("Échec à l'exécution de la transition", e);
				throw e;
			}
		}
	}

	protected _refresh() {
		if (this.trans && this.trans.isVisible(this.reg.env.srcView)) {
			DOM.setHidden(this, false);
			DOM.setAttrBool(this, 'disabled', !this.trans.isEnabled(this.reg.env.srcView));
			super._refresh();
		} else {
			DOM.setHidden(this, true);
		}
	}

}

//REG.reg.registerSkin("wsp-task-exec-trans", 1, /* language=CSS */ ``);

customElements.define("wsp-task-exec-trans", TaskExecTransBtn);


/** Requete auprès des champs pour connaitre du formulaire d'édition. */
export class InfoReqStateTask implements IInfo {

	isDirty?: boolean;
	isInError?: boolean;

	/** Si non null les fileds doivent renseigner les valeurs si ils sont dirty. */
	states: JTaskStates;

	infoHolders?: IInfoProducer[];
}

/** Notification d'une édition d'un champ. */
export class InfoChangeTask implements IInfo {

	protected _state: InfoReqStateTask;
	get state(): InfoReqStateTask {
		if (!this._state) {
			this._state = new InfoReqStateTask();
			this.field.reg.env.taskEditBroker.dispatchInfo(this._state, null);
		}
		return this._state;
	}

	constructor(public field: ITaskEditableField) {}

	infoHolders?: IInfoProducer[];
}


/** Requete pour constituer la liste des modifications à appliquer. */
export class InfoReqFillUpdates implements IInfo {
	updates: IUpdateTask[] = [];

	infoHolders?: IInfoProducer[];
}

interface ITaskEditableField {
	reg: IReg<ITaskUiEnv>
	fieldName: 'actTi' | 'tkDeadline' | 'scheduledDt' | 'lcSt' | 'tkDescription' | 'tkComment' | 'resps' | 'init'
	value?: any
}
